/*
 * File: useBusterTheHorseService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 30 Dec 2021 12:32:38 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import { WritableDraft } from 'immer/dist/internal';
import lodash from 'lodash';
import { DeliveryAddressModel } from 'models/DeliveryAddressModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
export interface GetDeliveryAddressesParams {
  dataAreaId: string | null;
  customerId: string | null;
  cityContains?: string | null;
  countryContains?: string | null;
  descriptionContains?: string | null;
  stateContains?: string | null;
  streetContains?: string | null;
  zipCodeContains?: string | null;
  offset: number;
  orderByField?: string;
}

export interface GetDeliveryAddressesResponseModel {
  data: DeliveryAddressModel[];
  totalCount: number;
}

interface DeliveryAddressParams {
  addressData: {
    dataAreaId: string;
    customerId: string;
    attentionTo?: string;
    city: string;
    country: string;
    county?: string;
    description?: string;
    state: string;
    street: string;
    zipCode: string;
  };
}

interface DeliveryAddressResponseModel {
  attentionTo: string;
  city: string;
  countryRegionISOCode: string;
  countryRegionId: string;
  county: string;
  description: string;
  formattedAddress: string;
  locationId: string;
  state: string;
  street: string;
  street2: string;
  zipCode: string;
}

/* ****************** Variables ****************** */
const busterTheHorseBaseUrl = process.env.REACT_APP_BUSTER_THE_HORSE_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_BUSTER_THE_HORSE;

const baseQuery = fetchBaseQuery({
  baseUrl: busterTheHorseBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const deliveryAddressApi = createApi({
  reducerPath: 'deliveryAddressApi',
  baseQuery,
  tagTypes: ['DeliveryAddresses'],
  endpoints: (builder) => ({
    getDeliveryAddresses: builder.query<GetDeliveryAddressesResponseModel, GetDeliveryAddressesParams>({
      query: ({ dataAreaId, offset, orderByField, customerId, cityContains, countryContains, descriptionContains, stateContains, streetContains, zipCodeContains }) => ({
        url: 'customerAddresses',
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          offset,
          orderByField,
          customerId: customerId ? encodeValue({ isUrl: false, value: customerId }) : undefined,
          cityContains: cityContains ? encodeValue({ isUrl: false, value: cityContains }) : undefined,
          countryContains: countryContains ? encodeValue({ isUrl: false, value: countryContains }) : undefined,
          descriptionContains: descriptionContains ? encodeValue({ isUrl: false, value: descriptionContains }) : undefined,
          stateContains: stateContains ? encodeValue({ isUrl: false, value: stateContains }) : undefined,
          streetContains: streetContains ? encodeValue({ isUrl: false, value: streetContains }) : undefined,
          zipCodeContains: zipCodeContains ? encodeValue({ isUrl: false, value: zipCodeContains }) : undefined
        }
      }),
      providesTags: ['DeliveryAddresses'],
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().deliveryAddressApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as GetDeliveryAddressesResponseModel | WritableDraft<GetDeliveryAddressesResponseModel>).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    createDeliveryAddress: builder.mutation<DeliveryAddressResponseModel, DeliveryAddressParams>({
      query: ({ addressData }) => ({
        url: 'customerAddress',
        method: 'POST',
        body: addressData
      }),
      invalidatesTags: ['DeliveryAddresses']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetDeliveryAddressesQuery, useCreateDeliveryAddressMutation, useGetVersionQuery, useGetApiNameQuery } = deliveryAddressApi;
